import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { useTranslation } from 'react-i18next';
import { Timer, AlertCircle, Upload } from 'lucide-react';
import { API_CONFIG } from '@/infrastructure/config/api';
import { get } from '@/shared/utils/fetchApi';
import { useToast } from '@/shared/components/ui/use-toast';

interface StorageTriggerWaitingDialogProps {
  isOpen: boolean;
  onClose: () => void;
  nodeId: string;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onStorageData: (data: any) => void;
  onTimeout: (nodeId: string) => void;
  workspaceId: string;
  organizationId: string;
  fileTypes?: string[];
  useSSE?: boolean; // Controla o uso de SSE ou polling
}

const StorageTriggerWaitingDialog: React.FC<StorageTriggerWaitingDialogProps> = ({
  isOpen,
  onClose,
  nodeId,
  onStorageData,
  onTimeout,
  workspaceId,
  organizationId,
  fileTypes = [],
  useSSE = false, // Por padrão, usar polling
}) => {
  const { t } = useTranslation();
  // toast kept for future use
  const { toast: _toast } = useToast();
  const INITIAL_TIMEOUT = 600; // 10 minutos em segundos
  const POLLING_INTERVAL = 3000; // 3 segundos

  const [elapsedTime, setElapsedTime] = useState(INITIAL_TIMEOUT);
  const [error, setError] = useState<string | null>(null);
  // isConnected é usado para controlar o estado da conexão SSE
  const [_isConnected, setIsConnected] = useState(false);
  const pollingInterval = useRef<NodeJS.Timeout | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const hasReceivedEvent = useRef(false);

  // Log das props recebidas
  useEffect(() => {
    if (isOpen) {
      console.log('StorageTriggerWaitingDialog props:');
      console.log('- nodeId:', nodeId);
      console.log('- organizationId:', organizationId);
      console.log('- workspaceId:', workspaceId);
      console.log('- fileTypes:', fileTypes);
    }
  }, [isOpen, nodeId, organizationId, workspaceId, fileTypes]);

  const navigateToStorage = () => {
    // Abre a página de Knowledge em uma nova aba
    window.open('http://localhost:3000/knowledge', '_blank');
    // Não fechar o diálogo - ele continua monitorando enquanto o usuário faz o upload
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const processEvent = useCallback(
    (event: MessageEvent) => {
      try {
        console.log('Processando evento SSE:', event);
        
        // Extrair os dados do evento
        let eventData;
        try {
          eventData = JSON.parse(event.data);
          console.log('Dados do evento parseados:', eventData);
        } catch (e) {
          console.error('Erro ao fazer parse dos dados do evento:', e);
          console.log('Dados brutos do evento:', event.data);
          return;
        }
        
        // Marcar que recebemos um evento para não mostrar erros de conexão
        hasReceivedEvent.current = true;
        
        // Verificar o tipo de evento
        const eventType = eventData.type;
        console.log('Tipo de evento recebido:', eventType);
        
        if (eventType === 'keep-alive') {
          console.log('Recebido evento keep-alive');
          return;
        }
        
        if (eventType === 'connecting' || eventType === 'connected') {
          console.log(`Conexão SSE: ${eventType}`);
          return;
        }
        
        if (eventType === 'file_event' || eventType === 'storage_event') {
          console.log('Evento de arquivo recebido:', eventData);
          
          // Formatar os dados para passar para o componente pai
          const formattedData = {
            ...eventData,
            nodeId: nodeId,
            organizationId: eventData.organizationId || organizationId,
            workspaceId: eventData.workspaceId || workspaceId,
            fileTypes: eventData.fileTypes || fileTypes,
            timestamp: new Date().toISOString(),
            organisationId: eventData.organisationId || organizationId, 
            eventType
          };
          
          console.log('Dados formatados para o componente pai:', formattedData);
          
          // Chamar o callback para notificar o componente pai
          onStorageData(formattedData);
          
          // Fechar o diálogo após processar o evento
          closeDialog();
          return;
        }
        
        console.log('Tipo de evento não reconhecido:', eventType);
      } catch (error) {
        console.error('Erro ao processar evento SSE:', error);
        setError(`Erro ao processar evento: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onStorageData, organizationId, workspaceId, fileTypes, nodeId]
  );

  // Função para fechar o diálogo e limpar recursos
  const closeDialog = () => {
    console.log('Fechando o diálogo e limpando recursos');
    
    // Limpar o intervalo de polling
    if (pollingInterval.current) {
      clearInterval(pollingInterval.current);
      pollingInterval.current = null;
    }
    
    // Fechar a conexão SSE
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    
    // Fechar o diálogo
    if (onClose) {
      onClose();
    }
  };

  useEffect(() => {
    let timer: NodeJS.Timeout;
    
    if (isOpen && nodeId) {
      console.log(`Iniciando conexão SSE para o nodeId: ${nodeId}`);
      
      if (useSSE) {
        try {
          console.log('Configurando conexão SSE...');
          
          // Se não tiver nodeId, não tentar conectar
          if (!nodeId) {
            console.log('NodeId não fornecido, não estabelecendo conexão SSE');
            return;
          }
          
          // Construir URL para conexão SSE
          let sseUrl = `${API_CONFIG.API_URL}/nodes/${nodeId}/events`;
          
          // Adicionar parâmetros à URL como query params
          const queryParams = new URLSearchParams();
          
          // Adicionar tipo de nó (obrigatório)
          queryParams.append('type', 'storage_trigger');
          
          if (organizationId) {
            queryParams.append('organizationId', organizationId);
          }
          
          if (workspaceId) {
            queryParams.append('workspaceId', workspaceId);
            console.log(`Adicionando workspaceId à URL SSE: ${workspaceId}`);
          } else {
            console.warn('workspaceId não fornecido para o StorageTriggerWaitingDialog');
          }
          
          if (fileTypes && fileTypes.length > 0) {
            queryParams.append('fileTypes', fileTypes.join(','));
            console.log(`Adicionando fileTypes à URL SSE: ${fileTypes.join(',')}`);
          }
          
          // Adicionar todos os parâmetros à URL
          sseUrl += `?${queryParams.toString()}`;
          
          console.log('URL SSE construída:', sseUrl);
          
          // Criar e configurar a conexão EventSource
          const eventSource = new EventSource(sseUrl);
          eventSourceRef.current = eventSource;
          
          // Configurar handlers de eventos
          eventSource.onopen = () => {
            console.log('Conexão SSE estabelecida com sucesso');
            setIsConnected(true);
          };
          
          eventSource.onerror = (err) => {
            console.error('Erro na conexão SSE:', err);
            setIsConnected(false);
            setError('Falha na conexão com o servidor. Por favor tente novamente.');
          };
          
          // Adicionar listeners para diferentes tipos de eventos
          eventSource.addEventListener('file_event', processEvent);
          eventSource.addEventListener('storage_event', processEvent);
          eventSource.addEventListener('keep-alive', processEvent);
          eventSource.addEventListener('completed', processEvent);
          eventSource.addEventListener('message', processEvent);
        } catch (error) {
          console.error('Erro ao configurar SSE:', error);
          setError(`Erro ao configurar conexão: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
        }
      } else {
        startPolling();
      }
      
      // Configurar timer para timeout
      timer = setInterval(() => {
        setElapsedTime((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            onTimeout(nodeId);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      return () => {
        clearInterval(timer);
        if (pollingInterval.current) {
          clearInterval(pollingInterval.current);
          pollingInterval.current = null;
        }
        if (eventSourceRef.current) {
          console.log('Fechando conexão SSE existente');
          // Remover todos os event listeners se o eventSource existir
          if (eventSourceRef.current) {
            eventSourceRef.current.removeEventListener('file_event', processEvent);
            eventSourceRef.current.removeEventListener('storage_event', processEvent);
            eventSourceRef.current.removeEventListener('keep-alive', processEvent);
            eventSourceRef.current.removeEventListener('completed', processEvent);
            eventSourceRef.current.removeEventListener('message', processEvent);
          }
          eventSourceRef.current.close();
          eventSourceRef.current = null;
        }
      };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, nodeId, onTimeout, useSSE, organizationId, workspaceId, fileTypes, processEvent]);

  const startPolling = () => {
    if (pollingInterval.current) {
      clearInterval(pollingInterval.current);
    }
    
    // Função para verificar eventos
    const checkEvents = async () => {
      try {
        const eventsUrl = `${API_CONFIG.API_URL}/nodes/${nodeId}/events?type=storage_trigger`;
        
        // Usar o get utility com parseJson: false para obter a resposta como texto
        const response = await get<Response>(eventsUrl, { parseJson: false }) as unknown as Response;
        
        if (!response.ok) {
          console.error('Erro ao verificar eventos:', response.statusText);
          return;
        }
        
        // Ler o texto da resposta em vez de assumir JSON diretamente
        const responseText = await response.text();
        console.log('Resposta bruta do SSE:', responseText);
        
        if (responseText.trim() === '') {
          console.log('Resposta vazia, ignorando');
          return;
        }
        
        // Processar cada linha de resposta SSE separadamente
        if (responseText.includes('data:')) {
          // Dividir a resposta em linhas
          const lines = responseText.split('\n');
          
          for (const line of lines) {
            const trimmedLine = line.trim();
            if (trimmedLine.startsWith('data:')) {
              try {
                // Extrair apenas a parte JSON após o 'data: '
                const jsonData = trimmedLine.substring(trimmedLine.indexOf(':') + 1).trim();
                if (!jsonData) continue;
                
                console.log('Tentando analisar JSON de SSE:', jsonData);
                const eventData = JSON.parse(jsonData);
                console.log('Evento SSE processado:', eventData);
                
                // Verificar se é um evento de arquivo
                if (eventData.type === 'file_event') {
                  processEvent({ data: JSON.stringify(eventData) } as unknown as MessageEvent);
                } else if (eventData.type === 'completed') {
                  // Se recebemos um evento de conclusão, processar diretamente
                  if (onStorageData) {
                    onStorageData(eventData);
                    
                    // Fechar diálogo e limpar recursos após o sucesso
                    console.log('Evento processado com sucesso, fechando diálogo');
                    onClose();
                  }
                }
              } catch (jsonError) {
                console.error('Erro ao analisar linha SSE:', jsonError, 'linha original:', trimmedLine);
              }
            }
          }
        } else {
          // Se não for SSE, tentar processar como JSON normal
          try {
            const data = JSON.parse(responseText);
            console.log('Resposta do polling (JSON normal):', data);
            
            // Verificar se é um evento de arquivo ou conclusão
            if (data.type === 'file_event' || data.type === 'completed') {
              processEvent({ data: JSON.stringify(data) } as unknown as MessageEvent);
            }
          } catch (jsonError) {
            console.error('Erro ao analisar resposta como JSON:', jsonError);
          }
        }
      } catch (error) {
        console.error('Erro durante o polling:', error);
      }
    };
    
    // Iniciar polling a cada 3 segundos
    pollingInterval.current = setInterval(checkEvents, POLLING_INTERVAL);
    
    // Verificar imediatamente na primeira vez
    checkEvents();
  };

  return (
    <Dialog open={isOpen} onOpenChange={closeDialog}>
      <DialogContent className="sm:max-w-[425px] p-0">
        <DialogHeader className="p-6 pb-0">
          <DialogTitle className="text-xl font-semibold tracking-tight">
            {t('workflow.storage_trigger.waiting_title', 'Aguardando Upload de Arquivo')}
          </DialogTitle>
        </DialogHeader>

        <div className="p-6 space-y-6">
          <div className="flex flex-col items-center space-y-4">
            {error ? (
              <>
                <AlertCircle className="h-12 w-12 text-destructive opacity-80" />
                <p className="text-sm text-destructive/90 text-center font-medium">
                  {error}
                </p>
              </>
            ) : (
              <>
                <div className="relative">
                  <Timer className="h-12 w-12 text-primary opacity-80 animate-pulse" />
                  <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-background px-2">
                    <p className="text-lg font-medium tabular-nums">
                      {formatTime(elapsedTime)}
                    </p>
                  </div>
                </div>
                <div className="h-4" /> {/* Espaçador */}
                <p className="text-sm text-muted-foreground text-center">
                  {t('workflow.storage_trigger.waiting_description', 'Aguardando o upload de um arquivo no storage. Clique no botão abaixo para ir à tela de upload.')}
                </p>
              </>
            )}

            <div className="w-full mt-6">
              <Button 
                className="w-full" 
                onClick={navigateToStorage} 
                variant="default"
              >
                <Upload className="mr-2 h-4 w-4" />
                {t('workflow.storage_trigger.go_to_upload', 'Ir para o Upload')}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StorageTriggerWaitingDialog;
